import React from 'react';
import { useTranslation } from 'react-i18next';
import { LessonNumberBadge } from '@/components/DiaryLessonCard/LessonNumberBadge';
import { LessonSection } from '@/components/DiaryLessonCard/LessonSection';
import { DiaryHomeTasks } from '@/components/DiaryHomeTasks';
import { DiaryEvaluations } from '@/components/DiaryTable/DiaryEvaluations';
import { DiaryTopics } from '@/components/DiaryTopics';
import { Door } from '@/components/Icons/Door';
import { Shuffle } from '@/components/Icons/Shuffle';
import { LessonScheduledTest } from '@/components/LessonScheduledTest';
import { LessonVideoMeeting } from '@/components/LessonVideoMeeting';
import { AttachmentsDisplay } from '@/types/components';
import { EMPTY_LESSON_SUBJECT, type DiaryLesson } from '@/types/diary';
import { hasDraftTopic, shouldIgnoreLessonClick } from '@/utils/diary';

interface Props {
  lesson: DiaryLesson;
  onClick: () => void;
}

export const DiaryLessonCard: React.FC<Props> = ({ lesson, onClick }) => {
  const { t } = useTranslation();
  const [videoMeeting] = lesson.videoMeetings;
  const [scheduledTest] = lesson.scheduledTests;
  const topicCount = lesson.topics.length;
  const topicTitle = hasDraftTopic(lesson.topics)
    ? t('diary.plannedLessonTopic', { count: topicCount })
    : t('diary.lessonTopic', { count: topicCount });
  const isEmptyLesson = lesson.className === EMPTY_LESSON_SUBJECT;
  const hasHomework =
    !!lesson.homeTasks.length || !!lesson.thirdPartyEvents.length;
  const topicAttachmentCount = lesson.topics.flatMap(
    ({ attachments }) => attachments,
  ).length;
  const homeworkAttachmentCount = lesson.homeTasks
    .filter(({ areAnswersEnabled }) => !areAnswersEnabled)
    .flatMap(({ attachments }) => attachments).length;

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (shouldIgnoreLessonClick(event)) {
      return;
    }

    onClick();
  };

  const changeMarker = lesson.isOnlyForCurrentWeek && (
    <Shuffle className="size-4 shrink-0 text-accent" />
  );

  if (isEmptyLesson) {
    return (
      <div className="flex items-center gap-3 px-4 py-2">
        <LessonNumberBadge lessonNumber={lesson.lessonNumber} isEmptyLesson />
        <span className="flex-1 text-subhead-14 text-subtle-foreground">
          {t('diary.emptyLesson')}
        </span>
        {changeMarker}
      </div>
    );
  }

  return (
    <div
      className="flex cursor-pointer flex-col rounded-xl bg-card p-4 shadow-card contrast:border contrast:border-border"
      onClick={handleClick}
    >
      <div className="flex items-center gap-3">
        <LessonNumberBadge lessonNumber={lesson.lessonNumber} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-subhead-14 text-foreground">
            {lesson.className}
          </span>
          {!!lesson.roomNumber && (
            <span className="mt-0.5 flex items-center gap-1 text-body-13 text-subtle-foreground">
              <Door className="size-4 shrink-0" />
              {lesson.roomNumber}
            </span>
          )}
        </div>
        {changeMarker}
      </div>

      {videoMeeting && (
        <>
          <div className="my-3 h-px bg-divider" />
          <LessonVideoMeeting meeting={videoMeeting} />
        </>
      )}

      {!!lesson.topics.length && (
        <LessonSection
          title={topicTitle}
          attachmentCount={topicAttachmentCount}
        >
          <DiaryTopics
            topics={lesson.topics}
            attachmentsDisplay={AttachmentsDisplay.Hidden}
            className="mt-1"
          />
        </LessonSection>
      )}

      {scheduledTest && (
        <>
          <div className="my-3 h-px bg-divider" />
          <LessonScheduledTest
            hasEmptyPlaceholder
            isAttachmentCountShown
            test={scheduledTest}
          />
        </>
      )}

      {hasHomework && (
        <LessonSection
          title={t('diary.table.homework')}
          attachmentCount={homeworkAttachmentCount}
        >
          <DiaryHomeTasks
            homeTasks={lesson.homeTasks}
            thirdPartyEvents={lesson.thirdPartyEvents}
            attachmentsDisplay={AttachmentsDisplay.Hidden}
            className="mt-1"
          />
        </LessonSection>
      )}

      {lesson.evaluations.length > 0 && (
        <>
          <div className="my-3 h-px bg-divider" />
          <DiaryEvaluations
            evaluations={lesson.evaluations}
            className="flex flex-col gap-2"
            itemClassName="rounded-[10px] bg-card px-4 py-3 shadow-card contrast:border contrast:border-border"
          />
        </>
      )}
    </div>
  );
};
